import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { ArrowRight } from "lucide-react";
import { WaitlistBand } from "../components/WaitlistBand";
import { SoftRibbon } from "../components/SoftRibbon";
import { usePublicMode } from "../hooks/usePublicMode";

const HERO_IMG = "https://images.unsplash.com/photo-1520515080697-c1f99f3137cf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwyfHxtb29keSUyMGdyZWVuaG91c2UlMjBpbnRlcmlvcnxlbnwwfHx8fDE3ODM5MTUwOTV8MA&ixlib=rb-4.1.0&q=85";
const STORY_IMG = "https://images.unsplash.com/photo-1682360516976-ffe79db7dff4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHw0fHxtb29keSUyMGdyZWVuaG91c2UlMjBpbnRlcmlvcnxlbnwwfHx8fDE3ODM5MTUwOTV8MA&ixlib=rb-4.1.0&q=85";

export default function HomePage({ startRibbon = false }) {
  const [featured, setFeatured] = useState([]);
  const { publicMode } = usePublicMode();
  const location = useLocation();

  useEffect(() => {
    api.get("/plants").then(({ data }) => setFeatured((data.plants || data || []).slice(0, 4))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO — original spacing preserved. Ribbon is absolutely positioned inside
          the top padding so it overlays the existing whitespace WITHOUT adding any
          height or pushing content down. */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-16 lg:pt-24 pb-24 relative">
        {(
          <div className="absolute top-4 lg:top-6 left-6 right-6 lg:left-12 lg:right-12 flex justify-center pointer-events-none">
            <div className="pointer-events-auto">
              <SoftRibbon key={`${location.key || "home"}-${startRibbon}`} start={startRibbon} />
            </div>
          </div>
        )}
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-5 pt-16">
            <div className="overline text-[var(--gu-muted)] mb-6">A house plant studio · Southern California</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter leading-[0.95]">
              Small green<br />
              lives, sent<br />
              <em className="not-italic text-[var(--gu-accent)]">home to yours.</em>
            </h1>
            <p className="mt-8 text-[var(--gu-muted)] leading-relaxed max-w-md">
              Hand-tended house plants, rooted and ready for a window in your home. Soon,
              other small growers will bring their leaves here too.
            </p>
            <div className="mt-10 flex items-center gap-6">
              <Link to="/shop" data-testid="hero-shop-cta" className="inline-flex items-center gap-3 bg-[var(--gu-primary)] text-[var(--gu-bg)] px-8 py-4 overline hover:bg-[var(--gu-primary)]/90 transition-colors">
                Browse the greenhouse <ArrowRight size={16} />
              </Link>
              <Link to="/about" className="overline text-[var(--gu-muted)] hover:text-[var(--gu-primary)] transition-colors border-b border-[var(--gu-border)] pb-1">
                Our story
              </Link>
            </div>
          </div>
          <div className="col-span-7 relative">
            <div className="aspect-[4/5] w-full overflow-hidden bg-[var(--gu-surface-2)]">
              <img src={HERO_IMG} alt="A moody greenhouse interior" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 lg:-left-16 bg-[var(--gu-bg)] px-6 py-4 border border-[var(--gu-border)] max-w-[280px] hidden sm:block">
              <div className="overline text-[var(--gu-muted)] mb-1">Notebook</div>
              <div className="font-display text-lg leading-tight">
                "Vulnerable things deserve a canopy held over them until they're ready."
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE / VALUES */}
      <section className="border-y border-[var(--gu-border)] bg-[var(--gu-surface-2)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-6 flex flex-wrap gap-x-16 gap-y-3 justify-between overline text-[var(--gu-primary)]">
          <span>—— Propagated by hand</span>
          <span>Roots checked twice</span>
          <span>Packed in recycled paper</span>
          <span>US shipping</span>
          <span>30-day leaf guarantee</span>
        </div>
      </section>

      {/* FEATURED PLANTS */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32">
        <div className="flex items-end justify-between mb-16">
          <div>
            <div className="overline text-[var(--gu-muted)] mb-4">This week in the studio</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-normal tracking-tight leading-[0.95]">
              Rooted and ready<br />for a window.
            </h2>
          </div>
          <Link to="/shop" className="hidden md:inline-flex items-center gap-2 overline hover:text-[var(--gu-accent)] transition-colors">
            View all <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-16">
          {Array.isArray(featured) && featured.map((p) => (
            <Link key={p.id} to={`/shop/${p.slug}`} data-testid={`featured-${p.slug}`} className="group block">
              <div className="aspect-[3/4] overflow-hidden bg-[var(--gu-surface-2)]">
                <img src={p.images?.[0]} alt={p.name} className="w-full h-full object-cover image-hover" />
              </div>
              <div className="mt-5 flex items-baseline justify-between">
                <div>
                  <div className="font-display text-xl leading-tight">{p.name}</div>
                  <div className="text-xs text-[var(--gu-muted)] mt-1 italic">{p.scientific_name}</div>
                </div>
                <div className="text-sm font-medium">${p.price.toFixed(0)}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* STORY BAND */}
      <section className="bg-[var(--gu-bg)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 grid grid-cols-12 gap-12 items-center">
          <div className="col-span-12 lg:col-span-6">
            <div className="aspect-[5/4] overflow-hidden bg-[var(--gu-surface-2)]">
              <img src={STORY_IMG} alt="A green walkway" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="col-span-12 lg:col-span-5 lg:col-start-8">
            <div className="overline text-[var(--gu-muted)] mb-6">The studio</div>
            <h2 className="font-display text-4xl lg:text-5xl font-light tracking-tight leading-tight">
              A quiet studio<br />in Southern California.
            </h2>
            <p className="mt-8 text-[var(--gu-muted)] leading-relaxed">
              An indoor greenhouse, picture windows facing the southern sun, and a shelf
              of grow-light stations for the ferns and calatheas that prefer the shade.
              Two rescue dogs. A canopy held over everything small.
            </p>
            <Link to="/about" className="mt-10 inline-block overline border-b border-[var(--gu-primary)] pb-1 hover:text-[var(--gu-accent)] transition-colors">
              Read the notebook
            </Link>
          </div>
        </div>
      </section>

      {/* WAITLIST */}
      <WaitlistBand />
    </div>
  );
}
